import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import './NavigationSection.css';

interface NavItem {
  path: string;
  label: string;
}

interface NavigationSectionProps {
  title: string;
  icon: React.ComponentType<{ size?: number }>;
  items: NavItem[];
  onNavigate?: () => void;
  isExpanded?: boolean;
}

const NavigationSection: React.FC<NavigationSectionProps> = ({
  title,
  icon: Icon,
  items,
  onNavigate,
  isExpanded = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const location = useLocation();

  // Reset clicked state when sidebar collapses
  React.useEffect(() => {
    if (!isExpanded) {
      setIsClicked(false);
      setIsOpen(false);
    }
  }, [isExpanded]);

  const isActive = (path: string): boolean => location.pathname === path;
  
  const isSectionActive = (): boolean => {
    return items.some(item => location.pathname.startsWith(item.path.split('/').slice(0, 2).join('/')));
  };

  const handleMouseEnter = (): void => {
    if (!isClicked) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = (): void => {
    if (!isClicked) {
      setIsOpen(false);
    }
  };

  const handleTriggerClick = (): void => {
    // Toggle dropdown when clicking the title
    setIsClicked(!isClicked);
    setIsOpen(!isOpen);
  };

  const handleItemClick = (): void => {
    // Keep dropdown open when clicking on a page item
    setIsClicked(true);
    setIsOpen(true);
    onNavigate?.();
  };

  return (
    <li 
      className="nav-section"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div 
        className={`nav-section__trigger ${isSectionActive() ? 'nav-section__trigger--active' : ''}`}
        onClick={handleTriggerClick}
      >
        <span className="nav-section__icon">
          <Icon size={20} />
        </span>
        {isExpanded && (
          <>
            <span className="nav-section__label">{title}</span>
            <span className={`nav-section__chevron ${isOpen ? 'nav-section__chevron--open' : ''}`}>
              <ChevronRight size={16} />
            </span>
          </>
        )}
      </div>
      
      {isOpen && isExpanded && (
        <ul className="nav-section__dropdown">
          {items.map(({ path, label }) => (
            <li key={path}>
              <Link
                to={path}
                className={`nav-section__dropdown-item ${isActive(path) ? 'nav-section__dropdown-item--active' : ''}`}
                onClick={handleItemClick}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default NavigationSection;
